import { FaArrowLeft } from "react-icons/fa";
import { debugLog } from "@/utils/debugLog";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext.tsx";
import {
  StudentInteractionTypes,
  trackStudentInteraction,
} from "../../../../utils/mixpanel-utils";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes";
import { persistNotepadCheckpoint } from "@/features/notepad/components/canvasApi";
import { useIsSlideNavigationLocked } from "@/features/tutor/hooks/slideNavigationLockHook";

const SKIP_TYPES = new Set(ASSESSMENT_KEYS);

const getSlideType = (slide) =>
  slide?.metadata?.slide_type ?? slide?.slide_type ?? null;

const findPrevContentIndex = (slides, fromIndex) => {
  for (let i = fromIndex - 1; i >= 0; i--) {
    if (!SKIP_TYPES.has(getSlideType(slides[i]))) return i;
  }
  return -1;
};

const PreviousBiteButton = () => {
  const { sessionId } = useSessionStore();
  const { emit } = useRuntimeContext();
  const currentSlideIndex = useSlideStore((state) => state.currentSlideIndex);
  const slides = useSlideStore((state) => state.slides);
  const handleChangeSlide = useSlideStore((state) => state.handleChangeSlide);
  const runManualSlideChange = useSlideStore(
    (state) => state.runManualSlideChange,
  );
  const isNavigationLocked = useIsSlideNavigationLocked();

  const prevContentIndex = findPrevContentIndex(slides, currentSlideIndex);

  if (prevContentIndex < 0) return null;

  const navigateToPrevious = async () => {
    await persistNotepadCheckpoint();
    emit("TURN_CHANGE");
    // Backend decides if this is a skip based on engagement threshold
    const slideChange = handleChangeSlide(sessionId, prevContentIndex, "user");
    trackStudentInteraction({
      type: StudentInteractionTypes.PREVIOUS_SLIDE,
    });
    return slideChange;
  };

  const handleClick = () => {
    debugLog("[PreviousBiteButton] Button clicked", { isNavigationLocked });

    // Silent, like Next Bite: this lock only ever means a slide change is
    // already under way, and "we're not done with this bite yet" — in the same
    // toast stack as the countdown — contradicts it.
    if (isNavigationLocked) {
      debugLog("[PreviousBiteButton] Button is locked, ignoring click");
      return;
    }

    runManualSlideChange(navigateToPrevious);
  };

  return (
    <button
      onClick={handleClick}
      title="Previous Bite"
      aria-disabled={isNavigationLocked}
      className={`w-[52.5px] h-[52.5px] rounded-full flex items-center justify-center focus:outline-none transition-colors duration-200 bg-[#F7F6FF] text-black ${
        isNavigationLocked
          ? "opacity-50 cursor-not-allowed"
          : "can-hover:hover:text-white can-hover:hover:bg-indigo-800 active:text-white active:bg-indigo-700"
      }`}
    >
      <FaArrowLeft className="w-4 h-4 text-eqblue" />
    </button>
  );
};

export default PreviousBiteButton;
